"use client";

/**
 * Render content as book_spreads units: a single page or a full spread on one canvas.
 */

import React from "react";
import { useEditorStore } from "./editorStore";
import CanvasEditor from "./CanvasEditor";

export default function ContentBookSpreadsCanvas({
  formats,
  pageWidthMm,
  fitViewport,
  zoomMode,
  manualZoom,
  onCopySelected,
  onPaste,
  onDuplicate,
  onElementSelect,
  onImagePlaceholderDoubleClick,
  onStageMetrics,
  onSelectionCenter,
  onDropAsset,
  onThumbnail,
  onThumbnailReady,
  forcedUnitIndex,
  thumbnailOnly,
  readOnly,
}: {
  formats: any[];
  pageWidthMm: number;
  fitViewport: { w: number; h: number };
  zoomMode: "fit" | "manual";
  manualZoom: number;
  onCopySelected?: () => void;
  onPaste?: () => void;
  onDuplicate?: () => void;
  onElementSelect?: () => void;
  onImagePlaceholderDoubleClick?: (payload: { doc: "content" | "cover"; pageIndex: number; elementId: string }) => void;
  onStageMetrics?: (m: { stageW: number; stageH: number }) => void;
  onSelectionCenter?: (p: { x: number; y: number } | null) => void;
  onThumbnail?: (unitId: string, dataUrl: string) => void;
  onThumbnailReady?: (args: { unitIndex: number; dataUrl: string; unitKey: string }) => void;
  forcedUnitIndex?: number;
  thumbnailOnly?: boolean;
  readOnly?: boolean;
  onDropAsset?: (payload: {
    asset_id: number;
    image_url?: string;
    width_px?: number;
    height_px?: number;
    x_mm: number;
    y_mm: number;
    targetDoc: "content" | "cover";
    targetUnitIndex: number;
  }) => void;
}) {
  const store = useEditorStore();
  const doc = store.contentData;
  const units = Array.isArray(doc?.pages) ? doc.pages : [];
  // English comment: In thumbnail preloader mode render a forced unit instead of the current selected one.
  const requestedUnitIndex = typeof forcedUnitIndex === "number" ? forcedUnitIndex : store.contentUnitIndex;
  const unitIndex = Math.max(0, Math.min(requestedUnitIndex, Math.max(0, units.length - 1)));
  const unit = units[unitIndex] || { kind: "single", elements: [] };
  const unitId = String(unit?.unit_id || `u_${unitIndex}`);
  const unitKey = `content:${unitId}`;
  const isThumbnailOnly = Boolean(thumbnailOnly);

  const isSpread = String(unit?.kind || "single") === "spread";
  const artboardWmm = isSpread ? 2 * pageWidthMm : pageWidthMm;
  const handleThumbnail = React.useCallback((key: string, dataUrl: string) => {
    onThumbnail?.(key, dataUrl);
    onThumbnailReady?.({ unitIndex, dataUrl, unitKey: key });
  }, [onThumbnail, onThumbnailReady, unitIndex]);

  return (
    <CanvasEditor
      doc="content"
      formats={formats}
      pageIndex={unitIndex}
      pageWidthMm={pageWidthMm}
      artboardWidthMmOverride={artboardWmm}
      showGutter={isSpread}
      gutterXmm={pageWidthMm}
      fitViewport={fitViewport}
      zoomMode={zoomMode}
      manualZoom={manualZoom}
      onCopySelected={isThumbnailOnly ? undefined : onCopySelected}
      onPaste={isThumbnailOnly ? undefined : onPaste}
      onDuplicate={isThumbnailOnly ? undefined : onDuplicate}
      onElementSelect={isThumbnailOnly ? undefined : onElementSelect}
      onImagePlaceholderDoubleClick={isThumbnailOnly ? undefined : onImagePlaceholderDoubleClick}
      onStageMetrics={isThumbnailOnly ? undefined : onStageMetrics}
      onSelectionCenter={isThumbnailOnly ? undefined : onSelectionCenter}
      onThumbnail={onThumbnail || onThumbnailReady ? handleThumbnail : undefined}
      unitId={unitKey}
      onDropAsset={isThumbnailOnly ? undefined : onDropAsset}
      onFocusPage={isThumbnailOnly ? undefined : (index) => store.setContentUnitIndex(index)}
      readOnly={readOnly || isThumbnailOnly}
      thumbnailOnly={isThumbnailOnly}
    />
  );
}
